import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Volume2, VolumeX, Wifi, WifiOff } from 'lucide-react'
import { toast } from 'sonner'

import type { SalesOrder } from '@/lib/api/sales-order-api'
import type {
    StationNewOrderEvent,
    StationNotificationEvent,
} from '@/hooks/use-station-sse'

import { StationOrderDetailDialog } from '@/components/station/station-order-detail-dialog'
import { useStationAudio } from '@/hooks/use-station-audio'
import { useStationEventTimes } from '@/hooks/use-station-event-times'
import { useStationSSE } from '@/hooks/use-station-sse'
import { useSalesOrders } from '@/hooks/use-sales-order-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import {
    orderActivityTime,
    rememberEventTime,
    sortOrdersByActivity,
} from '@/lib/station-order-sort'
import { buildNewOrderToast } from '@/lib/station-tts'

/** Thời gian tô sáng một đơn vừa được đọc loa. */
const HIGHLIGHT_MS = 5_000

export const Route = createFileRoute('/_app/_authed/station')({
    component: StationPage,
})

function StationPage() {
    // _authed beforeLoad guarantees user is authenticated before this renders
    return <StationView />
}

export function StationView() {
    const navigate = useNavigate()
    const exitKiosk = () => navigate({ to: '/', search: { kioskExit: true } })
    const {
        soundEnabled,
        audioBlocked,
        enableSound,
        disableSound,
        playNewOrderAlert,
        playNotificationAlert,
        playTestSound,
    } = useStationAudio()
    const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set())
    // Mốc sự kiện gần nhất theo đơn — quyết định thứ tự bảng. Lưu qua localStorage
    // để F5 giữa ca không xoá sạch thứ tự đã tích luỹ.
    const [eventTimes, setEventTimes] = useStationEventTimes()
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
    const highlightTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
        new Map(),
    )
    // Mốc hoạt động cao nhất đang hiển thị, dùng làm sàn cho sự kiện thiếu
    // `occurred_at`: đồng hồ tablet có thể chậm hơn server, tin `Date.now()`
    // thì đơn vừa được đọc loa lại không nhích lên.
    const maxActivityRef = useRef(0)

    useEffect(() => {
        const timers = highlightTimers.current
        return () => {
            for (const timer of timers.values()) clearTimeout(timer)
            timers.clear()
        }
    }, [])

    // Ghi nhận một đơn vừa có hoạt động: đẩy nó lên đầu bảng và tô sáng để
    // nhân viên nghe loa xong nhìn lên là thấy ngay.
    const markActivity = useCallback(
        (orderId: string | undefined, occurredAt: string | undefined) => {
            if (!orderId) return
            // +1ms để chắc chắn vượt lên trên đơn đang đứng đầu khi phải dùng
            // mốc dự phòng.
            const fallback = Math.max(Date.now(), maxActivityRef.current + 1)
            setEventTimes((prev) =>
                rememberEventTime(prev, orderId, occurredAt, fallback),
            )
            setHighlightedIds((prev) => new Set(prev).add(orderId))
            // Sự kiện thứ hai cho cùng đơn phải gia hạn vệt sáng, nếu không hẹn
            // giờ cũ vẫn nổ và tắt sáng sớm.
            const running = highlightTimers.current.get(orderId)
            if (running) clearTimeout(running)
            const timer = setTimeout(() => {
                setHighlightedIds((prev) => {
                    const next = new Set(prev)
                    next.delete(orderId)
                    return next
                })
                highlightTimers.current.delete(orderId)
            }, HIGHLIGHT_MS)
            highlightTimers.current.set(orderId, timer)
        },
        [],
    )

    const handleNewOrder = useCallback(
        (event: StationNewOrderEvent) => {
            playNewOrderAlert(event)
            // Key by order_id so a burst of orders (or a redelivered event)
            // updates one toast per order instead of stacking duplicates.
            toast.success(buildNewOrderToast(event), { id: event.order_id })
            if (event.speech_template) {
                markActivity(event.order_id, event.occurred_at)
            }
        },
        [playNewOrderAlert, markActivity],
    )

    const handleNotification = useCallback(
        (event: StationNotificationEvent) => {
            playNotificationAlert(event)
            // Chỉ xếp lại bảng khi sự kiện thật sự được đọc loa. Server chỉ đính
            // `speech_template` khi case đó đang bật trong cấu hình giọng nói,
            // nên không có nó nghĩa là đơn nhảy lên đầu trong im lặng.
            if (!event.speech_template) return
            // Soạn xong / nhận tiền xảy ra hàng giờ sau khi tạo đơn, nên đơn
            // phải được xếp lại theo mốc sự kiện chứ không theo giờ tạo.
            markActivity(event.order_id, event.occurred_at)
        },
        [playNotificationAlert, markActivity],
    )

    const { connected: sseConnected } = useStationSSE(
        handleNewOrder,
        handleNotification,
    )

    // Wake Lock — prevent tablet screen from sleeping
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null
        const request = async () => {
            try {
                wakeLock = await navigator.wakeLock.request('screen')
            } catch {
                /* not supported or denied */
            }
        }
        void request()
        const onVisible = () => {
            if (document.visibilityState === 'visible') void request()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            void wakeLock?.release()
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    const { data } = useSalesOrders({
        status: 'confirmed' as const,
        per_page: 100,
    })

    const { data: stockOutData } = useSalesOrders({
        status: 'stock_out' as const,
        per_page: 100,
    })

    const orders = useMemo(
        () =>
            sortOrdersByActivity(
                [...(data?.data ?? []), ...(stockOutData?.data ?? [])],
                eventTimes,
            ),
        [data?.data, stockOutData?.data, eventTimes],
    )

    useEffect(() => {
        maxActivityRef.current = orders.reduce(
            (max, order) => Math.max(max, orderActivityTime(order, eventTimes)),
            0,
        )
    }, [orders, eventTimes])

    return (
        <div className="min-h-screen bg-white text-gray-900 flex flex-col font-mono">
            {/* Header */}
            <header className="flex items-center justify-between px-6 py-3 bg-gray-100 border-b border-gray-300">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={exitKiosk}
                        aria-label={m.common_aria_back()}
                        className="inline-flex size-8 shrink-0 items-center justify-center rounded text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </button>
                    <h1 className="text-lg font-bold tracking-wide uppercase text-gray-800">
                        {m.station_title()}
                    </h1>
                    <span
                        className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${
                            sseConnected ? 'text-green-600' : 'text-red-500'
                        }`}
                    >
                        {sseConnected ? (
                            <Wifi className="h-3 w-3" />
                        ) : (
                            <WifiOff className="h-3 w-3" />
                        )}
                        {sseConnected
                            ? m.station_connected()
                            : m.station_disconnected()}
                    </span>
                </div>

                <div className="flex items-center gap-4">
                    <button
                        onClick={soundEnabled ? disableSound : enableSound}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${
                            soundEnabled && !audioBlocked
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : soundEnabled && audioBlocked
                                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 animate-pulse'
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200 animate-pulse'
                        }`}
                    >
                        {soundEnabled ? (
                            <Volume2 className="h-4 w-4" />
                        ) : (
                            <VolumeX className="h-4 w-4" />
                        )}
                        {soundEnabled && audioBlocked
                            ? m.station_audio_blocked()
                            : soundEnabled
                              ? m.station_sound_on()
                              : m.station_enable_sound()}
                    </button>
                    {soundEnabled && (
                        <button
                            onClick={playTestSound}
                            className="px-3 py-1.5 rounded text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                            {m.station_test_sound()}
                        </button>
                    )}
                    <StationClock />
                </div>
            </header>

            {/* Flight-board table */}
            <main className="flex-1 overflow-auto">
                {orders.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-400 text-lg">
                        {m.station_no_orders()}
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-300 bg-gray-50">
                                <th className="text-left py-2 px-4 w-20">
                                    {m.order_time()}
                                </th>
                                <th className="text-left py-2 px-4 w-36">
                                    {m.order_code()}
                                </th>
                                <th className="text-left py-2 px-4">
                                    {m.customer_title()}
                                </th>
                                <th className="text-left py-2 px-4">
                                    {m.order_shipping_address()}
                                </th>
                                <th className="text-right py-2 px-4 w-28">
                                    {m.order_total()}
                                </th>
                                <th className="text-center py-2 px-4 w-32">
                                    {m.order_status()}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => (
                                <StationRow
                                    key={order.id}
                                    order={order}
                                    activityAt={orderActivityTime(
                                        order,
                                        eventTimes,
                                    )}
                                    isHighlighted={highlightedIds.has(order.id)}
                                    onClick={() => setSelectedOrderId(order.id)}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </main>

            <StationOrderDetailDialog
                orderId={selectedOrderId}
                onClose={() => setSelectedOrderId(null)}
            />
        </div>
    )
}

function StationRow({
    order,
    activityAt,
    isHighlighted,
    onClick,
}: {
    order: SalesOrder
    activityAt: number
    isHighlighted: boolean
    onClick: () => void
}) {
    const isConfirmed = order.status === 'confirmed'
    const statusLabel = isConfirmed
        ? m.status_order_confirmed()
        : m.status_order_stock_out()

    // Mốc hoạt động, không phải giờ tạo — cùng khoá với thứ tự sắp xếp. Mốc 0
    // nghĩa là dữ liệu ngày hỏng: hiện gạch ngang thay vì "07:00" trông như thật.
    const timeStr = activityAt
        ? new Date(activityAt).toLocaleTimeString('vi-VN', {
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—'

    return (
        <tr
            onClick={onClick}
            className={`border-b border-gray-200 transition-colors duration-500 cursor-pointer ${
                isHighlighted ? 'bg-green-50 animate-pulse' : 'hover:bg-gray-50'
            }`}
        >
            {/* Time */}
            <td className="py-3 px-4 text-sm text-gray-500">{timeStr}</td>

            {/* Order code */}
            <td className="py-3 px-4">
                <span className="text-base font-bold text-gray-900 tracking-wide">
                    {order.order_code}
                </span>
            </td>

            {/* Customer */}
            <td className="py-3 px-4">
                <span className="text-sm text-gray-700">
                    {order.customer_name}
                </span>
            </td>

            {/* Address */}
            <td className="py-3 px-4">
                <span className="text-sm text-gray-500 truncate block max-w-xs">
                    {order.shipping_address || '—'}
                </span>
            </td>

            {/* Total */}
            <td className="py-3 px-4 text-right">
                <span className="text-sm font-medium text-gray-800">
                    {formatVnd(order.total_amount)}
                </span>
            </td>

            {/* Status */}
            <td className="py-3 px-4 text-center">
                <span
                    className={`inline-block text-xs font-bold px-2.5 py-1 rounded uppercase tracking-wide ${
                        isConfirmed
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                    }`}
                >
                    {statusLabel}
                </span>
            </td>
        </tr>
    )
}

function StationClock() {
    const [time, setTime] = useState(new Date())
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 30_000)
        return () => clearInterval(timer)
    }, [])
    return (
        <span className="text-lg text-gray-600">
            {time.toLocaleTimeString('vi-VN', {
                hour: '2-digit',
                minute: '2-digit',
            })}
        </span>
    )
}
